/*
 * YourXI kit generator
 * kitSVG(params, style) -> SVG string
 *
 * One shirt template, traced from the original master art (TOT - 8384- HOME.ai,
 * 170 x 190 pt artboard, 0.5 pt black hairline). Every kit is the same
 * silhouette; only colours, collar, cuffs and pattern layers change.
 *
 * No crests, badges, sponsors or maker marks are ever drawn.
 *
 * params = {
 *   body:        '#hex'                 main shirt colour
 *   sleeves:     '#hex'                 defaults to body
 *   raglan:      true|false             sleeve colour runs up to the collar (Arsenal 02-04)
 *   collar:      { type: 'crew'|'v'|'wrap'|'vinsert'|'polo'|'henley', color, trim, placket }
 *   cuff:        { color, width, trim, trimWidth }
 *   inside:      '#hex'                 lining colour seen at the back neck (defaults to body)
 *   pattern:     [ layer, layer, ... ]  drawn bottom to top, see PATTERNS below
 *   longSleeves: true|false             long sleeves hanging to the wrist (goalkeeper kits, older eras)
 * }
 *
 * style = 'A' | 'B' | 'C' | 'D'  (or an object, see STYLES)
 * opts  = { onDark: true }  optional third argument: lighter shadow + pale rim for dark backgrounds
 *
 * Custom kit maker: customKitSVG(opts, style, renderOpts), see the section above `api` at the bottom.
 */
(function (root) {
  'use strict';

  var W = 170, H = 214;
  // drawing on a dark background (opts.onDark): lighter shadow, pale rim behind the hairline
  var NIGHT = { bg: '#0E1420', shadow: '#9AA3B2', shadowOpacity: '.30', halo: '#FFFFFF', haloOpacity: '.42' };
  var mx = function (x) { return +(W - x).toFixed(2); };
  var uid = 0;

  /* ---------- colour helpers ---------- */
  function hexToRgb(h) {
    h = String(h || '#000').replace('#', '');
    if (h.length === 3) h = h.split('').map(function (c) { return c + c; }).join('');
    var n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function rgbToHex(r) {
    return '#' + r.map(function (v) {
      v = Math.max(0, Math.min(255, Math.round(v)));
      return (v < 16 ? '0' : '') + v.toString(16);
    }).join('');
  }
  function mix(a, b, t) {
    var A = hexToRgb(a), B = hexToRgb(b);
    return rgbToHex([0, 1, 2].map(function (i) { return A[i] + (B[i] - A[i]) * t; }));
  }
  function shade(c, t) { return t < 0 ? mix(c, '#000000', -t) : mix(c, '#ffffff', t); }
  function lum(c) {
    var r = hexToRgb(c).map(function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
    return 0.2126 * r[0] + 0.7152 * r[1] + 0.0722 * r[2];
  }

  /* ---------- geometry (from the .ai master) ---------- */
  var G = {
    NL: [58.85, 1.29], NR: [111.29, 1.29],
    SL: [24.01, 23.12],            // shoulder point
    CO: [0.23, 54.92],             // cuff, outer corner
    CI: [28.24, 78.86],            // cuff, inner corner
    AL: [35.88, 69.69],            // armpit
    hemY: 184, hemX: 32.3, hemMid: 190.4
  };

  // front neckline shapes per collar (outer edge = top edge of the body)
  var NECK = {
    crew: {
      outer: 'C110.68 2.29 100.59 21.38 85.08 21.38 C69.59 21.38 59.45 2.28 58.85 1.29',
      inner: 'C61.59 0.46 62.75 2.41 64.82 4.84 C68.69 9.37 75.69 16.69 85.07 16.69 C94.42 16.69 101.45 9.37 105.32 4.84 C107.39 2.41 108.55 0.46 108.64 0.3'
    },
    v: {
      outer: 'C109.6 4.2 93 25.5 85.08 33 C77.1 25.5 60.5 4.2 58.85 1.29',
      inner: 'C62.8 2.6 79.5 21 85.08 26.6 C90.6 21 107.3 2.6 108.64 0.3'
    }
  };

  function bodyPath(neck) {
    var n = NECK[neck] || NECK.crew;
    return 'M58.85 1.29' +
      ' C47 5.6 28 16 24.01 23.12' +
      ' C24.01 23.12 36.03 55.73 35.88 69.69' +
      ' C37.5 78 39.2 88 39.1 98' +
      ' C39 125 34.5 160 32.3 184' +
      ' C60 190.6 110 190.6 137.7 184' +
      ' C135.5 160 131 125 130.9 98' +
      ' C130.8 88 132.5 78 134.12 69.69' +
      ' C133.97 55.73 145.99 23.12 145.99 23.12' +
      ' C142 16 123 5.6 111.29 1.29 ' + n.outer + ' Z';
  }
  function sleevePath(side) {
    var f = side === 'R' ? mx : function (x) { return x; };
    return 'M' + f(24.01) + ' 23.12 L' + f(0.23) + ' 54.92 L' + f(28.24) + ' 78.86 L' + f(35.88) + ' 69.69' +
      ' C' + f(36.03) + ' 55.73 ' + f(24.01) + ' 23.12 ' + f(24.01) + ' 23.12 Z';
  }
  // long sleeve: same shoulder and armpit as the short sleeve, hanging down to a wrist cuff
  var LS = { O: [6.6, 131], I: [25.8, 129.6] };
  function longSleevePath(side) {
    var f = side === 'R' ? mx : function (x) { return x; };
    return 'M' + f(24.01) + ' 23.12 C' + f(14) + ' 34 ' + f(4) + ' 46 ' + f(1.6) + ' 60' +
      ' C' + f(0.6) + ' 84 ' + f(2.8) + ' 110 ' + f(LS.O[0]) + ' ' + LS.O[1] +
      ' L' + f(LS.I[0]) + ' ' + LS.I[1] +
      ' C' + f(26.4) + ' 110 ' + f(29.6) + ' 88 ' + f(35.88) + ' 69.69' +
      ' C' + f(36.03) + ' 55.73 ' + f(24.01) + ' 23.12 ' + f(24.01) + ' 23.12 Z';
  }
  // cuff band for the long sleeve, w deep, off from the wrist edge
  function longCuffPts(w, off) {
    off = off || 0;
    var o = LS.O, i = LS.I;
    return [[o[0] - 0.25 * off, o[1] - off], [i[0] + 0.03 * off, i[1] - off], [i[0] + 0.03 * (off + w), i[1] - off - w], [o[0] - 0.25 * (off + w), o[1] - off - w]];
  }
  function raglanPath(side) {
    var f = side === 'R' ? mx : function (x) { return x; };
    return 'M' + f(58.85) + ' 1.29 C' + f(47) + ' 5.6 ' + f(28) + ' 16 ' + f(24.01) + ' 23.12' +
      ' C' + f(24.01) + ' 23.12 ' + f(36.03) + ' 55.73 ' + f(35.88) + ' 69.69' +
      ' C' + f(41) + ' 46 ' + f(50) + ' 17 ' + f(62.4) + ' 6.6 Z';
  }
  function raglanSeam(side) {
    var f = side === 'R' ? mx : function (x) { return x; };
    return 'M' + f(35.88) + ' 69.69 C' + f(41) + ' 46 ' + f(50) + ' 17 ' + f(62.4) + ' 6.6';
  }
  var BACK_BAND = 'M61.5 0.1 C61.76 0.26 70.47 6.17 85.07 6.17 C99.53 6.17 108.38 0.26 108.65 0.09 L105.43 4.47 C105.43 4.47 98.2 9.25 85.07 9.25 C71.95 9.25 64.71 4.47 64.71 4.47 Z';
  function liningPath(neck) {
    var n = NECK[neck] || NECK.crew;
    return 'M61.5 0.3 ' + n.inner + ' C108.38 0.46 99.53 6.37 85.07 6.37 C70.47 6.37 61.76 0.46 61.5 0.3 Z';
  }
  function frontBandPath(neck) {
    var n = NECK[neck] || NECK.crew;
    return 'M61.5 0.3 ' + n.inner + ' L111.29 1.29 ' + n.outer + ' Z';
  }

  /* cuff band: strip of width w along the cuff edge */
  function lineX(p1, p2, p3, p4) {
    var d = (p1[0] - p2[0]) * (p3[1] - p4[1]) - (p1[1] - p2[1]) * (p3[0] - p4[0]);
    var t = ((p1[0] - p3[0]) * (p3[1] - p4[1]) - (p1[1] - p3[1]) * (p3[0] - p4[0])) / d;
    return [p1[0] + t * (p2[0] - p1[0]), p1[1] + t * (p2[1] - p1[1])];
  }
  function cuffPts(w, off) {
    off = off || 0;
    var u = [G.CI[0] - G.CO[0], G.CI[1] - G.CO[1]], L = Math.hypot(u[0], u[1]);
    var n = [u[1] / L, -u[0] / L]; // into the sleeve
    var a1 = [G.CO[0] + n[0] * off, G.CO[1] + n[1] * off], a2 = [G.CI[0] + n[0] * off, G.CI[1] + n[1] * off];
    var b1 = [G.CO[0] + n[0] * (off + w), G.CO[1] + n[1] * (off + w)], b2 = [G.CI[0] + n[0] * (off + w), G.CI[1] + n[1] * (off + w)];
    var p = [lineX(a1, a2, G.CO, G.SL), lineX(a1, a2, G.CI, G.AL), lineX(b1, b2, G.CI, G.AL), lineX(b1, b2, G.CO, G.SL)];
    return p;
  }
  function polyD(pts, side) {
    return 'M' + pts.map(function (p) { return (side === 'R' ? mx(p[0]) : +p[0].toFixed(2)) + ' ' + p[1].toFixed(2); }).join(' L') + ' Z';
  }
  function lineD(a, b, side) {
    var f = side === 'R' ? mx : function (x) { return +x.toFixed(2); };
    return 'M' + f(a[0]) + ' ' + a[1].toFixed(2) + ' L' + f(b[0]) + ' ' + b[1].toFixed(2);
  }

  /* ---------- collars ---------- */
  function collarParts(c, body, S) {
    var col = c.color || '#ffffff';
    var type = c.type || 'crew';
    var neck = (type === 'v' || type === 'wrap') ? 'v' : 'crew';
    var parts = [];
    parts.push({ d: frontBandPath(neck), fill: col, stroke: true, kind: 'collar' });
    if (c.trim) {
      // thin trim line sitting just inside the band's outer edge
      parts.push({ d: neck === 'v'
        ? 'M60.3 2.3 C62 4.6 78.6 23.6 85.08 30 C91.5 23.6 108 4.6 109.8 2.3'
        : 'M60.1 2.6 C62 6 72 19.3 85.08 19.3 C98.1 19.3 108.1 6 110 2.6',
        fill: 'none', line: c.trim, lw: 1.1, kind: 'trim' });
    }
    if (type === 'wrap') {
      // wrap-over V: the wearer's left lapel crosses over the right at the point of the V
      parts.push({ d: 'M108.64 0.3 L111.29 1.29 C104 10.5 93 23 81.6 35.6 L77.4 32.4 C88.6 21.2 101.8 8.6 108.64 0.3 Z', fill: col, stroke: true, kind: 'collar' });
    }
    if (type === 'vinsert') {
      parts.push({ d: 'M78.6 16.2 L91.5 16.2 L85.08 27.5 Z', fill: col, stroke: true, kind: 'collar' });
    }
    if (type === 'henley') {
      parts.push({ d: 'M81.4 18.6 L88.7 18.6 L88.7 40 L81.4 40 Z', fill: c.placket || col, stroke: true, kind: 'collar' });
      parts.push({ d: circle(85.05, 26, 1.05) + circle(85.05, 33.5, 1.05), fill: c.button || shade(c.placket || col, -0.35), kind: 'button' });
    }
    if (type === 'polo') {
      var pl = c.placket || body;
      parts.push({ d: 'M80.8 19.5 L89.3 19.5 L89.3 46 L80.8 46 Z', fill: pl, stroke: true, kind: 'collar' });
      parts.push({ d: circle(85.05, 29, 1.05) + circle(85.05, 38, 1.05), fill: c.button || shade(pl, lum(pl) > 0.5 ? -0.35 : 0.5), kind: 'button' });
      // fold-down flaps
      parts.push({ d: 'M58.3 0.9 C58.6 8 63.5 19 71.2 30.2 L85.08 21.4 C75 18.8 64.6 10 61.3 0.4 Z', fill: col, stroke: true, kind: 'collar' });
      parts.push({ d: 'M111.8 0.9 C111.5 8 106.6 19 98.9 30.2 L85.08 21.4 C95.1 18.8 105.5 10 108.8 0.4 Z', fill: col, stroke: true, kind: 'collar' });
      if (c.trim) {
        parts.push({ d: 'M60 3 C60.9 9.6 65.3 18.6 71.6 27.6 M110.1 3 C109.2 9.6 104.8 18.6 98.5 27.6', fill: 'none', line: c.trim, lw: 1.1, kind: 'trim' });
      }
    }
    return { neck: neck, parts: parts };
  }
  function circle(cx, cy, r) {
    return 'M' + (cx - r) + ' ' + cy + ' a' + r + ' ' + r + ' 0 1 0 ' + (2 * r) + ' 0 a' + r + ' ' + r + ' 0 1 0 ' + (-2 * r) + ' 0 Z';
  }

  /* ---------- pattern layers ----------
   * Each returns svg markup in shirt coordinates. `on` = 'body' | 'sleeves' | 'all' (clip target)
   */
  var PATTERNS = {
    // { type:'stripes', colors:[a,b], count:5, on:'all' }  vertical stripes, centred on the body
    stripes: function (p) {
      var n = p.count || 5, x0 = 24, x1 = 146, w = (x1 - x0) / n, out = '';
      var cols = p.colors;
      for (var i = -3; i < n + 3; i++) {
        var c = cols[((i % cols.length) + cols.length) % cols.length];
        out += rect(x0 + i * w, -5, w + 0.15, 200, c);
      }
      return out;
    },
    // { type:'pinstripes', color, spacing:6, width:0.6, full:true }  full: start far enough left to cover the sleeve at tight spacing
    pinstripes: function (p) {
      var s = p.spacing || 6, w = p.width || 0.6, out = '';
      for (var x = 85 - s * (p.full ? Math.max(20, Math.ceil(90 / s)) : 20); x < 175; x += s) out += rect(x - w / 2, -5, w, 200, p.color);
      return out;
    },
    // { type:'hoops', colors:[a,b], count:8 }
    hoops: function (p) {
      var n = p.count || 8, top = p.top != null ? p.top : 0, h = (192 - top) / n, out = '';
      for (var i = 0; i < n; i++) out += rect(-5, top + i * h, 180, h + 0.15, p.colors[i % p.colors.length]);
      return out;
    },
    // { type:'halves', color }  colours the wearer's left side (viewer's right)
    halves: function (p) { return rect(85.08, -5, 100, 200, p.color); },
    // { type:'quarters', color }  wearer's left top + right bottom
    quarters: function (p) { return rect(85.08, -5, 100, 100, p.color) + rect(-15, 95, 100.08, 100, p.color); },
    // { type:'sash', color, width:22, dir:'down-left' }
    sash: function (p) {
      var w = p.width || 22, d = p.dir === 'down-right';
      var a = d ? [0, 10] : [170, 10], b = d ? [170, 200] : [0, 200];
      return '<path d="M' + a[0] + ' ' + a[1] + ' L' + b[0] + ' ' + b[1] + '" stroke="' + p.color + '" stroke-width="' + w + '" fill="none"/>';
    },
    // { type:'chevron', color, y:46, width:12 }
    chevron: function (p) {
      var y = p.y || 46, w = p.width || 12, depth = p.depth || 24;
      return '<path d="M-5 ' + (y - 4) + ' L85.08 ' + (y + depth) + ' L175 ' + (y - 4) + '" stroke="' + p.color + '" stroke-width="' + w + '" fill="none" stroke-linejoin="miter"/>';
    },
    // { type:'sidePanels', color, width:9 }
    sidePanels: function (p) {
      var w = p.width || 9;
      var L = 'M-5 60 L' + (35.9 + w) + ' 66 C' + (37.5 + w) + ' 78 ' + (39.2 + w) + ' 88 ' + (39.1 + w) + ' 98 C' + (39 + w) + ' 125 ' + (34.5 + w) + ' 160 ' + (32.3 + w) + ' 194 L-5 194 Z';
      var R = 'M175 60 L' + mx(35.9 + w) + ' 66 C' + mx(37.5 + w) + ' 78 ' + mx(39.2 + w) + ' 88 ' + mx(39.1 + w) + ' 98 C' + mx(39 + w) + ' 125 ' + mx(34.5 + w) + ' 160 ' + mx(32.3 + w) + ' 194 L175 194 Z';
      return '<path d="' + L + R + '" fill="' + p.color + '"/>';
    },
    // { type:'band', color, y:78, h:10 }  horizontal chest band
    band: function (p) { return rect(-5, p.y != null ? p.y : 78, 180, p.h || 9, p.color); },
    // { type:'pinlines', color, ys:[110,118,126], width:1.6 }
    pinlines: function (p) {
      return (p.ys || []).map(function (y) { return rect(-5, y, 180, p.width || 1.6, p.color); }).join('');
    },
    // { type:'piping', color, width:1.4, inset:7 }  raglan-to-side curve (France 2006)
    piping: function (p) {
      var i = p.inset != null ? p.inset : 7, lw = p.width || 1.4;
      var L = 'M62.4 6.6 C50 17 41 46 ' + (35.9 + i * 0.4) + ' 70 C' + (38 + i) + ' 84 ' + (40 + i) + ' 100 ' + (39.5 + i) + ' 118 C' + (39 + i) + ' 145 ' + (35 + i) + ' 168 ' + (33.5 + i) + ' 192';
      var R = 'M' + mx(62.4) + ' 6.6 C' + mx(50) + ' 17 ' + mx(41) + ' 46 ' + mx(35.9 + i * 0.4) + ' 70 C' + mx(38 + i) + ' 84 ' + mx(40 + i) + ' 100 ' + mx(39.5 + i) + ' 118 C' + mx(39 + i) + ' 145 ' + mx(35 + i) + ' 168 ' + mx(33.5 + i) + ' 192';
      return '<path d="' + L + ' ' + R + '" stroke="' + p.color + '" stroke-width="' + lw + '" fill="none"/>';
    },
    // { type:'shoulderStripes', colors:['#fff'], count:3, gap:1.6, width:1.4 }
    // generic stripes from collar to cuff along the top of the sleeve
    shoulderStripes: function (p) {
      var n = p.count || 3, gap = p.gap || 1.6, lw = p.width || 1.4, out = '';
      var cols = p.colors || [p.color || '#fff'];
      for (var k = 0; k < n; k++) {
        var o = 2.2 + k * (lw + gap);
        var c = cols[k % cols.length];
        var dL = 'M' + (61 - o * 0.2) + ' ' + (1.8 + o) + ' C' + (42 + o * 0.5) + ' ' + (5.5 + o) + ' ' + (26 + o * 0.6) + ' ' + (11 + o * 0.9) + ' ' + (24.2 + o * 0.6) + ' ' + (23.5 + o * 0.45) + ' L' + (0.5 + o * 0.62) + ' ' + (55.3 + o * 0.5);
        var dR = dL.replace(/([ML]|C| )(-?[\d.]+) (-?[\d.]+)/g, function (m, c0, x, y) { return c0 + mx(+x) + ' ' + y; });
        out += '<path d="' + dL + ' ' + dR + '" stroke="' + c + '" stroke-width="' + lw + '" fill="none"/>';
      }
      return out;
    },
    // { type:'hipFlash', color }  short diagonal flashes at the hips (Arsenal 02-04)
    hipFlash: function (p) {
      var L = 'M39.6 118.9 L57.8 127.6 L57.2 129.1 L39.2 121.3 Z';
      var R = 'M' + mx(39.6) + ' 118.9 L' + mx(57.8) + ' 127.6 L' + mx(57.2) + ' 129.1 L' + mx(39.2) + ' 121.3 Z';
      return '<path d="' + L + R + '" fill="' + p.color + '"/>';
    },
    // { type:'yoke', color, y:34, width:1 }  thin seam line across the chest
    yoke: function (p) {
      var y = p.y || 34;
      return '<path d="M20 ' + y + ' C55 ' + (y + 3) + ' 115 ' + (y + 3) + ' 150 ' + y + '" stroke="' + p.color + '" stroke-width="' + (p.width || 1) + '" fill="none"/>';
    },
    // { type:'gradient', colors:[top, bottom], dir:'vertical'|'diagonal' }  smooth fade (custom kits)
    gradient: function (p) {
      var id = 'gr' + (++uid) + Math.random().toString(36).slice(2, 5);
      var d = p.dir === 'diagonal' ? 'x1="0" y1="0" x2="1" y2="1"' : 'x1="0" y1="0" x2="0" y2="1"';
      var c = p.colors || ['#ffffff', '#000000'];
      return '<defs><linearGradient id="' + id + '" ' + d + '><stop offset="0" stop-color="' + c[0] + '"/><stop offset="1" stop-color="' + c[1] + '"/></linearGradient></defs>' +
        rect(-5, -5, 180, 200, 'url(#' + id + ')');
    },
    // { type:'dots', color, size:2.6, spacing:12 }  polka dots on a staggered grid
    dots: function (p) {
      var r = p.size || 2.6, s = p.spacing || 12, out = '', row = 0;
      for (var y = 4; y < 196; y += s * 0.87, row++)
        for (var x = (row % 2 ? s / 2 : 0) - s; x < 175; x += s) out += '<circle cx="' + x.toFixed(2) + '" cy="' + y.toFixed(2) + '" r="' + r + '" fill="' + p.color + '"/>';
      return out;
    },
    // { type:'checks', color, size:14 }  chequerboard (Croatia style)
    checks: function (p) {
      var s = p.size || 14, out = '';
      for (var y = -5, j = 0; y < 196; y += s, j++)
        for (var x = 85.08 - s * Math.ceil(90 / s), i = 0; x < 175; x += s, i++) if ((i + j) % 2 === 0) out += rect(x, y, s + 0.1, s + 0.1, p.color);
      return out;
    },
    // { type:'sleeveBand', color, w:4, off:8 }  band around the sleeve above the cuff
    sleeveBand: function (p) {
      var pts = cuffPts(p.w || 4, p.off || 8);
      return '<path d="' + polyD(pts) + ' ' + polyD(pts, 'R') + '" fill="' + p.color + '"/>';
    }
  };
  function rect(x, y, w, h, c) {
    return '<rect x="' + (+x.toFixed(2)) + '" y="' + (+y.toFixed(2)) + '" width="' + (+w.toFixed(2)) + '" height="' + (+h.toFixed(2)) + '" fill="' + c + '"/>';
  }

  /* ---------- styles ---------- */
  var STYLES = {
    A: { // faithful to the current prints
      name: 'Faithful', stroke: '#111111', sw: 0.7, seams: true, cuffLine: false,
      shadow: 'soft', inside: 0, shading: false, stitches: false, sleeveTone: 0
    },
    B: { // clean flat, no outline
      name: 'Clean flat', stroke: null, sw: 0, seams: false, cuffLine: false,
      shadow: 'flat', inside: -0.28, shading: false, stitches: false, sleeveTone: -0.05, round: 0.9
    },
    C: { // fabric shading and folds
      name: 'Soft fabric', stroke: '#1d1d1d', sw: 0.55, seams: true, cuffLine: false,
      shadow: 'soft', inside: -0.3, shading: true, stitches: false, sleeveTone: 0
    },
    D: { // line + fill
      name: 'Line and fill', stroke: '#141414', sw: 2.1, seams: true, cuffLine: true,
      shadow: 'ink', inside: -0.22, shading: false, stitches: true, sleeveTone: 0
    }
  };

  /* ---------- main ---------- */
  function kitSVG(params, style, opts) {
    var DARK = !!((opts && opts.onDark) || (params && params.onDark));
    var S = typeof style === 'object' ? style : (STYLES[style] || STYLES.A);
    var P = params || {};
    var id = 'k' + (++uid) + Math.random().toString(36).slice(2, 6);
    var body = P.body || '#ffffff';
    var sleeves = P.sleeves || body;
    var col = collarParts(P.collar || {}, body, S);
    var neck = col.neck;
    var inside = shade(P.inside || body, S.inside);
    var sleeveFill = S.sleeveTone ? shade(sleeves, S.sleeveTone) : sleeves;

    var LONG = !!P.longSleeves;
    var bodyD = bodyPath(neck), slL = LONG ? longSleevePath('L') : sleevePath('L'), slR = LONG ? longSleevePath('R') : sleevePath('R');
    var cuffAt = LONG ? longCuffPts : cuffPts;
    var shirtD = bodyD + ' ' + slL + ' ' + slR;
    var strokeAttr = S.stroke ? ' stroke="' + S.stroke + '" stroke-width="' + S.sw + '" stroke-linejoin="round" stroke-linecap="round"' : '';
    var softAttr = S.round ? ' stroke-width="' + S.round + '" stroke-linejoin="round"' : '';
    function piece(d, fill, stroked) {
      var st = stroked && S.stroke ? strokeAttr : (S.round ? ' stroke="' + shade(fill, lum(fill) > 0.6 ? -0.13 : -0.06) + '"' + softAttr : '');
      return '<path d="' + d + '" fill="' + fill + '"' + st + '/>';
    }

    var defs = '<clipPath id="' + id + 'b"><path d="' + bodyD + '"/></clipPath>' +
      '<clipPath id="' + id + 's"><path d="' + slL + ' ' + slR + '"/></clipPath>' +
      '<clipPath id="' + id + 'a"><path d="' + shirtD + '"/></clipPath>' +
      '<filter id="' + id + 'f" x="-50%" y="-200%" width="200%" height="500%"><feGaussianBlur stdDeviation="3.2"/></filter>' +
      '<filter id="' + id + 'g" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="2.4"/></filter>' +
      '<filter id="' + id + 'h" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="6"/></filter>';

    var out = [];
    // shadow
    if (S.shadow === 'soft') out.push(DARK
      ? '<ellipse cx="85.08" cy="201" rx="47" ry="4.2" fill="' + NIGHT.shadow + '" opacity="' + NIGHT.shadowOpacity + '" filter="url(#' + id + 'f)"/>'
      : '<ellipse cx="85.08" cy="201" rx="47" ry="4.2" fill="#000" opacity=".30" filter="url(#' + id + 'f)"/>');
    // on a dark background: a pale rim behind the silhouette keeps the black hairline readable
    if (DARK && S.stroke) out.push('<path d="' + shirtD + ' ' + liningPath(neck) + ' ' + BACK_BAND + '" fill="none" stroke="' + NIGHT.halo + '" stroke-opacity="' + NIGHT.haloOpacity + '" stroke-width="' + (S.sw * 4.5) + '" stroke-linejoin="round"/>');
    if (S.shadow === 'flat') out.push('<ellipse cx="85.08" cy="200" rx="44" ry="3.4" fill="' + (DARK ? NIGHT.shadow : '#000') + '" opacity="' + (DARK ? '.18' : '.07') + '"/>');
    if (S.shadow === 'ink') out.push('<ellipse cx="85.08" cy="201" rx="50" ry="3.6" fill="#141414" opacity=".13"/>');

    // inside of the shirt + back neck band
    out.push(piece(liningPath(neck), inside, true));
    out.push(piece(BACK_BAND, (P.collar && P.collar.back) || (P.collar && P.collar.color) || '#ffffff', true));

    // base fills
    out.push(piece(bodyD, body, false));
    out.push(piece(slL, sleeveFill, false));
    out.push(piece(slR, sleeveFill, false));

    // pattern layers
    (P.pattern || []).forEach(function (layer) {
      var fn = PATTERNS[layer.type];
      if (!fn) return;
      var on = layer.on || ({ sleeveBand: 'sleeves', shoulderStripes: 'all', sash: 'all', hoops: 'all', halves: 'all', quarters: 'all', gradient: 'all', dots: 'all', checks: 'all' })[layer.type] || 'body';
      var clip = on === 'sleeves' ? 's' : on === 'all' ? 'a' : 'b';
      out.push('<g clip-path="url(#' + id + clip + ')">' + fn(layer) + '</g>');
    });

    // raglan sleeve panels
    if (P.raglan) {
      out.push('<g clip-path="url(#' + id + 'b)">' + piece(raglanPath('L') + ' ' + raglanPath('R'), sleeveFill, false) + '</g>');
    }

    // cuffs
    var cuff = P.cuff || {};
    if (cuff.color) {
      var cp = cuffAt(cuff.width || 3.2, 0);
      out.push('<path d="' + polyD(cp) + ' ' + polyD(cp, 'R') + '" fill="' + cuff.color + '"/>');
      if (cuff.trim) {
        var tp = cuffAt(cuff.trimWidth || 1, (cuff.width || 3.2));
        out.push('<path d="' + polyD(tp) + ' ' + polyD(tp, 'R') + '" fill="' + cuff.trim + '"/>');
      }
      if (S.cuffLine && S.stroke) {
        var e = cuffAt(cuff.width || 3.2, 0);
        out.push('<path d="' + lineD(e[3], e[2]) + ' ' + lineD(e[3], e[2], 'R') + '" fill="none"' + strokeAttr.replace('stroke-width="' + S.sw + '"', 'stroke-width="' + (S.sw * 0.6) + '"') + '/>');
      }
    }

    // fabric shading (style C)
    if (S.shading) {
      var sh = [];
      var dark = lum(body) < 0.05 ? 0.22 : 0.12;
      sh.push('<rect x="0" y="0" width="170" height="192" fill="url(#' + id + 'lg)"/>');
      // armpit and waist creases
      sh.push('<g filter="url(#' + id + 'g)" fill="none" stroke="#000" stroke-linecap="round">' +
        '<path d="M38 74 C44 84 50 92 58 98" stroke-width="3.2" opacity="' + dark + '"/>' +
        '<path d="M132.2 74 C126.2 84 120.2 92 112.2 98" stroke-width="3.2" opacity="' + dark + '"/>' +
        '<path d="M41 116 C47 121 53 123 62 124" stroke-width="2.6" opacity="' + (dark * 0.8) + '"/>' +
        '<path d="M129.2 116 C123.2 121 117.2 123 108.2 124" stroke-width="2.6" opacity="' + (dark * 0.8) + '"/>' +
        '<path d="M52 168 C56 175 59 181 61 187" stroke-width="2.2" opacity="' + (dark * 0.7) + '"/>' +
        '<path d="M116 170 C113 176 111 181 110 187" stroke-width="2" opacity="' + (dark * 0.6) + '"/>' +
        '<path d="M16 40 C19 50 24 60 30 68" stroke-width="2.4" opacity="' + (dark * 0.9) + '"/>' +
        '<path d="M154 40 C151 50 146 60 140 68" stroke-width="2.4" opacity="' + (dark * 0.9) + '"/>' +
        '</g>');
      // highlights
      sh.push('<g filter="url(#' + id + 'h)" fill="#fff">' +
        '<ellipse cx="66" cy="54" rx="15" ry="24" opacity=".16"/>' +
        '<ellipse cx="104" cy="62" rx="9" ry="18" opacity=".08"/>' +
        '<ellipse cx="14" cy="42" rx="5" ry="10" opacity=".10" transform="rotate(37 14 42)"/>' +
        '</g>');
      defs += '<linearGradient id="' + id + 'lg" x1="0" x2="1" y1="0" y2="0">' +
        '<stop offset="0" stop-color="#000" stop-opacity=".16"/>' +
        '<stop offset=".22" stop-color="#000" stop-opacity=".02"/>' +
        '<stop offset=".5" stop-color="#fff" stop-opacity=".05"/>' +
        '<stop offset=".78" stop-color="#000" stop-opacity=".03"/>' +
        '<stop offset="1" stop-color="#000" stop-opacity=".18"/></linearGradient>' +
        '<linearGradient id="' + id + 'vg" x1="0" x2="0" y1="0" y2="1">' +
        '<stop offset="0" stop-color="#fff" stop-opacity=".07"/>' +
        '<stop offset=".7" stop-color="#000" stop-opacity="0"/>' +
        '<stop offset="1" stop-color="#000" stop-opacity=".10"/></linearGradient>';
      sh.push('<rect x="0" y="0" width="170" height="192" fill="url(#' + id + 'vg)"/>');
      out.push('<g clip-path="url(#' + id + 'a)">' + sh.join('') + '</g>');
    }

    // collar on top
    col.parts.forEach(function (pt) {
      if (pt.kind === 'trim') {
        out.push('<path d="' + pt.d + '" fill="none" stroke="' + pt.line + '" stroke-width="' + pt.lw + '" stroke-linecap="round"/>');
      } else {
        out.push(piece(pt.d, pt.fill, pt.stroke));
      }
    });
    if (S.shading) {
      // soft shadow cast by the collar onto the chest
      out.push('<g clip-path="url(#' + id + 'b)"><path d="' + (neck === 'v' ? 'M60 3 L85 36 L110 3' : 'M60 3 C66 18 76 24 85 24 C94 24 104 18 110 3') + '" stroke="#000" stroke-width="3" opacity=".10" fill="none" filter="url(#' + id + 'g)"/></g>');
    }

    // outlines and seams
    if (S.stroke) {
      out.push('<path d="' + bodyD + '" fill="none"' + strokeAttr + '/>');
      out.push('<path d="' + slL + ' ' + slR + '" fill="none"' + strokeAttr + '/>');
      if (P.raglan && S.seams) out.push('<path d="' + raglanSeam('L') + ' ' + raglanSeam('R') + '" fill="none"' + strokeAttr + '/>');
      col.parts.forEach(function (pt) { if (pt.stroke) out.push('<path d="' + pt.d + '" fill="none"' + strokeAttr + '/>'); });
    }
    if (S.stitches) {
      var st = ' fill="none" stroke="' + S.stroke + '" stroke-width="0.7" stroke-dasharray="2.2 1.8" stroke-linecap="round" opacity=".55"';
      out.push('<path d="M36.5 179.6 C62 185.6 108 185.6 133.6 179.6"' + st + '/>');
      var a = cuffAt(0.1, (cuff.color ? (cuff.width || 3.2) : 0) + 3.4);
      out.push('<path d="' + lineD(a[0], a[1]) + ' ' + lineD(a[0], a[1], 'R') + '"' + st + '/>');
    }

    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="-4 -4 ' + (W + 8) + ' ' + (H + 4) + '" class="kit">' +
      '<defs>' + defs + '</defs>' + out.join('') + '</svg>';
  }

  /* ---------- custom kit maker ----------
   * customKitSVG(opts, style, renderOpts) -> SVG string (style defaults to 'A')
   * customKitParams(opts) -> kitSVG params (for callers that want the raw layers)
   *
   * opts = {
   *   body, sleeves, collar, cuffs, trim, patternColor   '#hex' (sleeves -> body, collar -> white,
   *                                                       cuffs -> collar, trim -> none)
   *   collarStyle: 'crew' | 'vneck' | 'polo' | 'wrap'
   *   sleeve:      'short' | 'long'
   *   pattern:     one of KIT_PATTERNS ids
   *   variant:     pattern option (stripes thin|medium|wide, hoops thin|wide, sash left|right,
   *                gradient vertical|diagonal, dots small|medium|large)
   *   scale:       0.5 - 2, size of the repeat (1 = default, 2 = twice as big / fewer, 0.5 = finer)
   *   onDark:      true when drawn on a dark background
   * }
   * The pattern also covers the sleeves when they are the body colour; with contrast
   * sleeves it stays on the body. Shoulder stripes always run onto the sleeves.
   */
  var KIT_PATTERNS = [
    { id: 'plain', label: 'Plain', scale: false },
    { id: 'stripes', label: 'Stripes', scale: true, variants: [['thin', 'Thin'], ['medium', 'Medium'], ['wide', 'Wide']], variant: 'medium' },
    { id: 'pinstripes', label: 'Pinstripes', scale: true },
    { id: 'hoops', label: 'Hoops', scale: true, variants: [['thin', 'Thin'], ['wide', 'Wide']], variant: 'wide' },
    { id: 'halves', label: 'Halves', scale: false },
    { id: 'quarters', label: 'Quarters', scale: false },
    { id: 'sash', label: 'Sash', scale: true, variants: [['left', 'From left shoulder'], ['right', 'From right shoulder']], variant: 'left' },
    { id: 'chevron', label: 'Chevron', scale: true },
    { id: 'gradient', label: 'Fade', scale: false, variants: [['vertical', 'Vertical'], ['diagonal', 'Diagonal']], variant: 'vertical' },
    { id: 'dots', label: 'Polka dots', scale: true, variants: [['small', 'Small'], ['medium', 'Medium'], ['large', 'Large']], variant: 'medium' },
    { id: 'checks', label: 'Checks', scale: true },
    { id: 'sidePanels', label: 'Side panels', scale: true },
    { id: 'shoulderStripes', label: 'Shoulder stripes', scale: true }
  ];
  var KIT_DEFAULTS = {
    body: '#C8102E', sleeves: null, collar: '#FFFFFF', cuffs: null, trim: null, patternColor: '#FFFFFF',
    collarStyle: 'crew', sleeve: 'short', pattern: 'plain', variant: null, scale: 1
  };
  var KIT_SWATCHES = [
    { name: 'Red', hex: '#C8102E' }, { name: 'Claret', hex: '#7A263A' }, { name: 'Maroon', hex: '#4F1A24' },
    { name: 'Pink', hex: '#F4A6C1' }, { name: 'Magenta', hex: '#D6006E' }, { name: 'Purple', hex: '#5B2C83' },
    { name: 'Navy', hex: '#14213D' }, { name: 'Royal blue', hex: '#034694' }, { name: 'Azure', hex: '#0072CE' },
    { name: 'Sky blue', hex: '#6CABDD' }, { name: 'Teal', hex: '#008C95' }, { name: 'Bottle green', hex: '#0B4D2C' },
    { name: 'Green', hex: '#00843D' }, { name: 'Lime', hex: '#9BCB3B' }, { name: 'Yellow', hex: '#FFD100' },
    { name: 'Amber', hex: '#FFB81C' }, { name: 'Gold', hex: '#C8A24A' }, { name: 'Tangerine', hex: '#FF9E1B' },
    { name: 'Orange', hex: '#EF6C00' }, { name: 'Cream', hex: '#F3EBD3' }, { name: 'White', hex: '#FFFFFF' },
    { name: 'Silver grey', hex: '#A7A9AC' }, { name: 'Charcoal', hex: '#3C3C3C' }, { name: 'Black', hex: '#111111' }
  ];
  var COLLAR_MAP = { crew: 'crew', vneck: 'v', v: 'v', polo: 'polo', wrap: 'wrap' };

  function customKitParams(o) {
    o = o || {};
    var get = function (k) { return o[k] != null && o[k] !== '' ? o[k] : KIT_DEFAULTS[k]; };
    var body = get('body'), sleeves = get('sleeves') || body, pc = get('patternColor');
    var collar = get('collar'), cuffs = get('cuffs') || collar, trim = get('trim');
    var pat = get('pattern'), meta = KIT_PATTERNS.filter(function (x) { return x.id === pat; })[0] || KIT_PATTERNS[0];
    var v = o.variant || meta.variant;
    var k = Math.max(0.5, Math.min(2, +get('scale') || 1));
    var same = String(sleeves).toLowerCase() === String(body).toLowerCase();
    var on = same ? 'all' : 'body';
    var odd = function (n) { n = Math.max(3, Math.round(n)); return n % 2 ? n : n + 1; };
    var L = null;
    switch (meta.id) {
      case 'stripes': L = { type: 'stripes', colors: [body, pc], count: odd(({ thin: 13, medium: 9, wide: 5 })[v] / k) }; break;
      case 'pinstripes': L = { type: 'pinstripes', color: pc, spacing: 7 * k, width: 0.55 + 0.4 * k, full: true }; break;
      case 'hoops': L = { type: 'hoops', colors: [body, pc], count: Math.max(3, Math.round((v === 'thin' ? 16 : 8) / k)) }; break;
      case 'halves': L = { type: 'halves', color: pc }; break;
      case 'quarters': L = { type: 'quarters', color: pc }; break;
      case 'sash': L = { type: 'sash', color: pc, dir: v === 'right' ? 'down-left' : 'down-right', width: 26 * k }; break;
      case 'chevron': L = { type: 'chevron', color: pc, width: 13 * k, depth: 26 }; on = 'body'; break;
      case 'gradient': L = { type: 'gradient', colors: [body, pc], dir: v === 'diagonal' ? 'diagonal' : 'vertical' }; break;
      case 'dots': { var r = ({ small: 1.9, medium: 2.8, large: 4.2 })[v] * k; L = { type: 'dots', color: pc, size: +r.toFixed(2), spacing: +(r * 4.2).toFixed(2) }; break; }
      case 'checks': L = { type: 'checks', color: pc, size: 15 * k }; break;
      case 'sidePanels': L = { type: 'sidePanels', color: pc, width: 9 * k }; on = 'body'; break;
      case 'shoulderStripes': L = { type: 'shoulderStripes', colors: [pc], count: 3, width: 1.5 * k, gap: 1.5 * k }; on = 'all'; break;
    }
    if (L) L.on = on;
    var type = COLLAR_MAP[get('collarStyle')] || 'crew';
    return {
      body: body, sleeves: sleeves, longSleeves: get('sleeve') === 'long', onDark: !!o.onDark,
      pattern: L ? [L] : [],
      collar: { type: type, color: collar, trim: trim && trim !== collar ? trim : undefined, placket: type === 'polo' ? collar : undefined },
      cuff: { color: cuffs, width: 2.6, trim: trim && trim !== cuffs ? trim : undefined, trimWidth: 1 }
    };
  }
  function customKitSVG(o, style, renderOpts) {
    return kitSVG(customKitParams(o), style || 'A', renderOpts || (o && o.onDark ? { onDark: true } : null));
  }

  var api = {
    kitSVG: kitSVG, STYLES: STYLES, PATTERNS: Object.keys(PATTERNS), shade: shade,
    customKitSVG: customKitSVG, customKitParams: customKitParams,
    KIT_PATTERNS: KIT_PATTERNS, KIT_DEFAULTS: KIT_DEFAULTS, KIT_SWATCHES: KIT_SWATCHES, NIGHT: NIGHT
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else { root.kitSVG = kitSVG; root.YourXIKits = api; }
})(this);
